import { cn } from "@/lib/utils";
import type { InterviewType } from "@/services/interview.service";
import {
  formatDuration,
  formatInterviewDate,
  formatInterviewTimeRange,
  interviewDisplayState,
  interviewTypeLabel,
  INTERVIEW_STATE_META,
  relativeDayLabel,
  safeMeetingLink,
} from "@/utils/interview.utils";
import {
  Building2,
  CalendarDays,
  ChevronRight,
  Clock,
  Code2,
  ExternalLink,
  Hourglass,
  Info,
  MapPin,
  Phone,
  RefreshCw,
  Users,
  Video,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

const TYPE_ICONS: Record<string, LucideIcon> = {
  phone: Phone,
  video: Video,
  in_person: Building2,
  technical: Code2,
  hr: Users,
};

/**
 * The backend keeps interviews attached to withdrawn/rejected applications in
 * the full list (history stays complete) — only `?upcoming=true` filters them
 * out. Joining the parent status client-side stops those rounds reading as live.
 */
const STALE_APPLICATION_NOTES: Record<string, string> = {
  withdrawn: "You withdrew this application, so this round will not go ahead.",
  rejected: "This application was not taken forward.",
};

interface InterviewCardProps {
  interview: InterviewType;
  applicationStatus?: string;
  /** Hidden on the detail page, where the link would point at the current route. */
  showDetailLink?: boolean;
}

const InterviewCard = ({
  interview,
  applicationStatus,
  showDetailLink = true,
}: InterviewCardProps) => {
  const state = interviewDisplayState(interview);
  const meta = INTERVIEW_STATE_META[state];
  const TypeIcon = TYPE_ICONS[interview.interview_type] ?? CalendarDays;

  const link = safeMeetingLink(interview.meeting_link);
  const duration = formatDuration(interview.duration_minutes);
  const isLive = state === "upcoming" || state === "awaiting_update";
  const countdown = state === "upcoming" ? relativeDayLabel(interview.scheduled_at) : null;

  const staleNote = isLive
    ? STALE_APPLICATION_NOTES[applicationStatus ?? ""]
    : undefined;
  const note = staleNote ?? meta.note;
  const muted = meta.muted || !!staleNote;

  return (
    <div
      className={cn(
        "group overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:border-indigo-100 hover:shadow-md",
        muted && "opacity-70 hover:opacity-100"
      )}
    >
      <div className={cn("h-1 w-full", meta.accent)} />

      <div className="p-5 sm:p-6">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50">
              <TypeIcon size={18} className="text-indigo-500" />
            </div>
            <div className="min-w-0">
              <p className="mb-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                {interview.company_name || "Company not shared"}
              </p>
              <p className="line-clamp-1 text-sm font-bold leading-tight text-slate-800">
                {interview.internship_title || "Internship role"}
              </p>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-indigo-600">
                {interviewTypeLabel(interview.interview_type)}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider",
                meta.chip
              )}
            >
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full",
                  meta.dot,
                  state === "upcoming" && "animate-pulse"
                )}
              />
              {meta.label}
            </span>

            {interview.reschedule_count > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full border border-violet-100 bg-violet-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-700">
                <RefreshCw size={9} />
                Rescheduled
              </span>
            )}
          </div>
        </div>

        {/* Always renders, even for a phone round where link and venue are both null. */}
        <div className="mb-4 space-y-2 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <CalendarDays size={13} className="shrink-0 text-gray-400" />
            <span className="font-semibold text-slate-700">
              {formatInterviewDate(interview.scheduled_at)}
            </span>
            {countdown && (
              <span className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
                {countdown}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Clock size={13} className="shrink-0 text-gray-400" />
            <span>
              {formatInterviewTimeRange(
                interview.scheduled_at,
                interview.duration_minutes
              )}
            </span>
          </div>

          {duration && (
            <div className="flex items-center gap-2">
              <Hourglass size={13} className="shrink-0 text-gray-400" />
              <span>{duration}</span>
            </div>
          )}

          {interview.venue && (
            <div className="flex items-start gap-2">
              <MapPin size={13} className="mt-0.5 shrink-0 text-gray-400" />
              <span>{interview.venue}</span>
            </div>
          )}

          {link && "text" in link && (
            <div className="flex items-start gap-2">
              <Info size={13} className="mt-0.5 shrink-0 text-gray-400" />
              <span className="break-all">
                Joining details:{" "}
                <span className="font-semibold text-slate-700">{link.text}</span>
              </span>
            </div>
          )}
        </div>

        {note && (
          <div
            className={cn(
              "mb-4 flex items-start gap-2 rounded-xl border px-3 py-2 text-xs font-medium",
              state === "cancelled" || staleNote
                ? "border-rose-100 bg-rose-50 text-rose-600"
                : state === "awaiting_update"
                  ? "border-amber-100 bg-amber-50 text-amber-700"
                  : "border-slate-200 bg-slate-50 text-slate-600"
            )}
          >
            <Info size={13} className="mt-0.5 shrink-0" />
            <span>{note}</span>
          </div>
        )}

        <div className="flex gap-2 border-t border-gray-100 pt-4">
          {/* Only an http(s) link becomes a button — anything else stayed as text above. */}
          {link && "href" in link && isLive && !staleNote && (
            <a
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-indigo-600 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
            >
              Join <ExternalLink size={14} />
            </a>
          )}

          <Link
            to={`/discover/internships/${interview.internship_id}`}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-200 py-2 text-sm font-semibold text-gray-700 transition hover:border-gray-300 hover:bg-gray-50"
          >
            View Role <ChevronRight size={14} />
          </Link>

          {showDetailLink && (
            <Link
              to={`/interviews/${interview.id}`}
              className="flex items-center justify-center rounded-xl border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-500 transition hover:border-indigo-200 hover:text-indigo-600"
              aria-label="View interview details"
            >
              <ChevronRight size={16} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterviewCard;
